import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SkipLink } from './components/SkipLink';
import { ProtectedRoute } from './components/ProtectedRoute';
import { usePageTracking } from './hooks/usePageTracking';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Product } from './pages/Product';
import { WeatherStation } from './pages/WeatherStation';
import { Blogs } from './pages/Blogs';
import { Contact } from './pages/Contact';
import WhatsAppAuth from './pages/WhatsAppAuth';
import SimpleDashboard from './pages/SimpleDashboard';
import WeatherStationForm from './pages/WeatherStationForm';
import DhartiNetraForm from './pages/DhartiNetraForm';
import SimpleSettings from './pages/SimpleSettings';

// GA4 Tracking Component
const RouteTracker = () => {
  usePageTracking();
  return null;
};

// Inner component that reads ThemeContext (must be inside ThemeProvider)
const ThemedApp: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          // Brand green as primary color — consistent with CSS vars
          colorPrimary: '#16a34a',
          colorSuccess: '#16a34a',
          borderRadius: 8,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          // Dark mode surface tokens to match our slate palette
          ...(isDark && {
            colorBgBase: '#0f172a',
            colorBgContainer: '#1e293b',
            colorBgElevated: '#334155',
            colorBorder: '#334155',
            colorText: '#f1f5f9',
            colorTextSecondary: '#94a3b8',
          }),
        },
        components: {
          Button: {
            controlHeight: 40,
            controlHeightLG: 48,
          },
          Input: {
            controlHeight: 40,
            controlHeightLG: 48,
          },
        },
      }}
    >
      <BrowserRouter>
        <RouteTracker />
        <SkipLink />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/products/dhartinetra" element={<Product />} />
          <Route path="/products/weather-station" element={<WeatherStation />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Navigate to="/whatsapp-login" replace />} />
          <Route path="/register" element={<Navigate to="/whatsapp-login" replace />} />
          <Route path="/whatsapp-login" element={<WhatsAppAuth />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <SimpleDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/weather-station"
            element={
              <ProtectedRoute>
                <WeatherStationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/dhartinetra"
            element={
              <ProtectedRoute>
                <DhartiNetraForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <SimpleSettings />
              </ProtectedRoute>
            }
          />
          {/* Legacy route redirects */}
          <Route path="/product" element={<Navigate to="/products/dhartinetra" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <LanguageProvider>
            <ThemedApp />
          </LanguageProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
